import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { PieChart as RechartsPieChart, Pie, Cell, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, LabelList, Legend } from 'recharts';
import { reportStyles } from '../../shared/globalSX';
import { CHARACTER_LIMITS } from '../../shared/appConstants';
import useCloseOnScroll from '../../shared/useCloseOnScroll';

export const PieChartView = ({ data, height = 350, outerRadius: customOuterRadius }) => {
  const chartRef = useRef(null);
  const [tooltipEnabled, setTooltipEnabled] = useState(false);
  
  // Responsive layout hooks
  const isLargeScreen = useMediaQuery('(min-width: 1441px)');
  
  // On large screens, standard radius is 120. On small screens/zoomed displays, we decrease it to 95.
  const outerRadius = customOuterRadius !== undefined ? customOuterRadius : (isLargeScreen ? 120 : 95);
  
  const total = data.reduce((sum, d) => sum + d.tickets, 0);
  const showLegend = data.length <= 15;
  const isNormalView = outerRadius !== 200; // Normal view has outerRadius 120/95, enlarge view has 200
  
  // Guard against division by zero
  const getPercentage = (tickets) => {
    if (total === 0) return '0';
    return ((tickets / total) * 100).toFixed(1);
  };

  const closeTooltip = useCallback(() => {
    setTooltipEnabled(false);
  }, []);

  const handleLegendWheel = useCallback((event) => {
    const container = event.currentTarget;
    const canScrollHorizontally = container.scrollWidth > container.clientWidth;
    if (!canScrollHorizontally) return;

    const scrollDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (scrollDelta === 0) return;

    event.preventDefault();
    event.stopPropagation();
    container.scrollLeft += scrollDelta;
  }, []);

  useCloseOnScroll(chartRef, closeTooltip);
  
  const renderLegend = (props) => {
    const { payload } = props;
    if (!payload || !payload.length) return null;
    
    // Keep normal chart legends on a single horizontal row; the container scrolls if needed.
    // For enlarge view: keep original wrapping behavior.
    if (isNormalView) {
      return (
        <Box className="reports-pie-legend-container" onWheel={handleLegendWheel}>
          <Box className="reports-pie-legend-inner">
            <Box className="reports-pie-legend-row">
              {payload.map((entry, index) => {
                const displayName = String(entry.value || '');
                const maxLength = CHARACTER_LIMITS.PIE_CHART_LABEL_TRUNCATE_LIMIT;
                const truncatedName = displayName.length > maxLength ? displayName.substring(0, maxLength) + '...' : displayName;
                const percentage = getPercentage(entry.payload.tickets);
                
                return (
                  <Box
                    key={`legend-${entry.value || index}`}
                    className="reports-pie-legend-item"
                    title={displayName}
                    data-legend-item="true"
                    data-legend-value={entry.value || ''}
                  >
                    <Box
                      className="reports-pie-legend-color-box"
                      sx={{ backgroundColor: entry.color }}
                    />
                    <Typography variant="caption" className="reports-pie-legend-text">
                      {truncatedName} ({percentage}%)
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    }
    
    // Original behavior for enlarge view
    return (
      <Box className="reports-pie-legend-enlarge">
        {payload.map((entry, index) => {
          const displayName = String(entry.value || '');
          const maxLength = CHARACTER_LIMITS.PIE_CHART_LABEL_TRUNCATE_LIMIT;
          const truncatedName = displayName.length > maxLength ? displayName.substring(0, maxLength) + '...' : displayName;
          const percentage = getPercentage(entry.payload.tickets);
          
          return (
            <Box
              key={`legend-${entry.value || index}`}
              className="reports-pie-legend-item"
              title={displayName}
              data-legend-item="true"
              data-legend-value={entry.value || ''}
            >
              <Box
                className="reports-pie-legend-color-box"
                sx={{ backgroundColor: entry.color }}
              />
              <Typography className="reports-pie-legend-text">
                {truncatedName} ({percentage}%)
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
  <Box
    ref={chartRef}
    sx={{ height }}
    onMouseMove={() => setTooltipEnabled(true)}
    onMouseLeave={closeTooltip}
  >
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart>
        <Pie
          data={data}
          cx="50%"
          cy={outerRadius === 200 ? (showLegend ? "45%" : "50%") : (showLegend ? (isLargeScreen ? "51%" : "50%") : "50%")}
          innerRadius={outerRadius * 0.6}
          outerRadius={outerRadius}
          paddingAngle={3}
          cornerRadius={4}
          dataKey="tickets"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        {showLegend && (
          <Legend 
            content={renderLegend}
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: '20px' }}
          />
        )}
        <RechartsTooltip 
          active={tooltipEnabled}
          allowEscapeViewBox={{ x: false, y: false }}
          wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
          isAnimationActive={false}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              const percentage = getPercentage(data.tickets);
              return (
                <Box sx={reportStyles.chartTooltip}>
                  <Box component="p" sx={reportStyles.chartTooltipText}>{`${data.name}: ${data.tickets} (${percentage}%)`}</Box>
                </Box>
              );
            }
            return null;
          }} 
        />
      </RechartsPieChart>
    </ResponsiveContainer>
  </Box>
);
};

export const BarChartView = ({ data, height = 380, barFill, variant }) => {
  const chartRef = useRef(null);
  const [tooltipEnabled, setTooltipEnabled] = useState(false);
  // Guard against empty data array
  const maxValue = data.length > 0 ? Math.max(...data.map(d => d.tickets)) : 0;
  const yAxisDomain = [0, Math.max(10, Math.ceil(maxValue * 1.2))];
  const showLabels = data.length <= 15;
  const isStatusVariant = variant === 'status';

  const closeTooltip = useCallback(() => {
    setTooltipEnabled(false);
  }, []);

  useCloseOnScroll(chartRef, closeTooltip);
  
  const truncateLabel = (label) => {
    const str = String(label || '');
    return str.length > 10 ? str.substring(0, 9) + '...' : str;
  };
  
  const CustomTick = ({ x, y, payload }) => {
    const label = truncateLabel(payload.value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="end"
          dominantBaseline="middle"
          transform="rotate(-45 0 0)"
          fontSize={14}
        >
          {label}
        </text>
      </g>
    );
  };

  return (
  <Box
    ref={chartRef}
    sx={{ height, overflow: 'visible' }}
    onMouseMove={() => setTooltipEnabled(true)}
    onMouseLeave={closeTooltip}
  >
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart data={data} margin={{ top: 35, right: 30, left: 20, bottom: isStatusVariant ? 10 : 10 }}>
        <CartesianGrid strokeDasharray="5 5" vertical={false} />
        <XAxis
          dataKey="name"
          tick={isStatusVariant ? false : (showLabels ? <CustomTick /> : false)}
          interval={0}
          height={isStatusVariant ? 10 : 100}
          axisLine={true}
          tickLine={false}
          />
        <YAxis 
          domain={yAxisDomain} 
          axisLine={true}
          tickLine={false}
          tick={{ fontSize: 13, fill: '#64748b' }}
        />
        <RechartsTooltip 
          active={tooltipEnabled}
          allowEscapeViewBox={{ x: false, y: false }}
          wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
          isAnimationActive={false}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return (
                <Box sx={reportStyles.chartTooltip}>
                  <Box component="p" sx={reportStyles.chartTooltipText}>{`${data.name}: ${data.tickets}`}</Box>
                </Box>
              );
            }
            return null;
          }}
        />
        <Bar 
          dataKey="tickets" 
          fill={isStatusVariant ? '#9381ff' : (barFill || '#082B72')}
          radius={isStatusVariant ? [50, 50, 50, 50] : [6, 6, 0, 0]}
          barSize={isStatusVariant ? 50 : undefined}
          isAnimationActive={false}
        >
          {!isStatusVariant && !barFill && data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
          {isStatusVariant ? (
            <LabelList 
              dataKey="name" 
              content={(props) => {
                const { x, y, width, height, value } = props;
                // Threshold for a typical vertical label (approx 80px)
                const isTooSmall = height < 80;
                
                return (
                  <g transform={`translate(${x + width / 2}, ${isTooSmall ? y - 10 : y + height / 2})`}>
                    <text
                      x={0}
                      y={0}
                      fill={isTooSmall ? '#082B72' : 'white'}
                      textAnchor={isTooSmall ? "start" : "middle"}
                      dominantBaseline="middle"
                      transform="rotate(-90)"
                      style={{ 
                        fontWeight: 600, 
                        fontSize: '13px', 
                        pointerEvents: 'none',
                        textShadow: isTooSmall ? 'none' : '0px 1px 2px rgba(0,0,0,0.2)' 
                      }}
                    >
                      {value}
                    </text>
                  </g>
                );
              }}
            />
          ) : (
            <LabelList dataKey="tickets" position="top" offset={10} style={{ fontSize: '13px', fontWeight: 600, fill: '#64748b' }} />
          )}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  </Box>
);
};
